import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyIcon } from '@heroicons/react/24/outline';
import studentAuthService from '../../../services/studentAuth';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await studentAuthService.forgotPassword(phoneNumber);
      setSuccess(true);
      // Redirect to OTP verification after 2 seconds
      setTimeout(() => {
        navigate('/student/verify-otp', { state: { phoneNumber } });
      }, 2000);
    } catch (err) {
      // Handle validation errors (422) from backend
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to send OTP. Please try again.';

      if (detail) {
        // If detail is an array (validation errors from Pydantic)
        if (Array.isArray(detail)) {
          // Extract all error messages
          const errors = detail.map(error => error.msg || error.message).filter(Boolean);
          errorMessage = errors.length > 0 ? errors.join('. ') : errorMessage;
        }
        // If detail is a string (custom HTTPException)
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <KeyIcon className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Forgot Password?</h1>
            <p className="text-gray-400">
              Enter your phone number to receive an OTP
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-4">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 bg-green-900/20 border border-green-700 rounded-lg p-4">
              <p className="text-green-300 text-sm">
                ✓ OTP sent to your registered email! Redirecting...
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="Enter your 10-digit phone number"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength="10"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/student/login')}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
